import { AgGridReact } from 'ag-grid-react';
import React, { useState } from 'react';
import * as Bixby from '$/core';
import { AnalyticsService, UserActivityService } from '$/services';
import { toastError } from '$/store/toast/actions';
import '../index.scss';
import { connect } from 'react-redux';
import dayjs from 'dayjs';
import SimpleTextFilter from '$/pages/client/compliance/UserActivity/SimpleTextFilter';
import { FilterModel, GridApi } from 'ag-grid-enterprise';
import BixButton from '$/components/global/bixButton';
import { downloadFile } from '$/store/export/actions';
import { AppState } from '$/store';
import { ReactComponent as DownloadIcon } from '$/assets/icons/download.svg';
import FilterButtons, { IFilterButton } from '$/components/filterButtons';

interface IActivityHistoryProps {
  profile?: Bixby.Models.Profile;
  dispatchToastError: (message: string) => void;
  dispatchDownloadData: (blob: Blob, fileName: string) => Promise<void>;
}
interface GetRowsParams {
  startRow: number;
  filterModel: Partial<Bixby.Models.UserActivity>;
}

const PUBLIC_DECLARATION_LABEL = 'Public Declaration';
const ALL_DECLARATION_LABEL = 'All Declaration';
const PRIVATE_DECLARATION_LABEL = 'Private Declaration';

const UserActivity: React.FC<IActivityHistoryProps> = ({ dispatchToastError, dispatchDownloadData, profile }) => {
  const [pageSize] = useState(50);
  const [gridApi, setGridApi] = useState<GridApi | undefined>();
  const [isDownloading, setIsDownloading] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [declarationFilters, setDeclarationFilters] = useState([
    { id: ALL_DECLARATION_LABEL, name: ALL_DECLARATION_LABEL, selected: true },
    { id: PRIVATE_DECLARATION_LABEL, name: PRIVATE_DECLARATION_LABEL, selected: false },
    { id: PUBLIC_DECLARATION_LABEL, name: PUBLIC_DECLARATION_LABEL, selected: false },
  ] as IFilterButton[]);

  const filterModelToFilters = (filterModel: FilterModel) => {
    return Object.keys(filterModel)
      .flatMap(field => {
        const filter = filterModel[field];

        if (filter.filterType === 'set') {
          return {
            field,
            value: filter.values.join(', '),
            operator: Bixby.Enums.Operator.Equals,
          } as Bixby.Models.UserActivityFilter;
        }
        if (filter.filterType === 'date') {
          if (filter.type === 'inRange') {
            return [
              {
                field: `${field}From`,
                value: new Date(filter.dateFrom),
                operator: Bixby.Enums.Operator.Equals,
              },
              {
                field: `${field}To`,
                value: new Date(filter.dateTo),
                operator: Bixby.Enums.Operator.Equals,
              },
            ] as Bixby.Models.UserActivityFilter[];
          } else {
            return [
              {
                field: `${field}From`,
                value: new Date(filter.dateFrom),
                operator: Bixby.Enums.Operator.Equals,
              },
              {
                field: `${field}To`,
                value: new Date(filter.dateFrom),
                operator: Bixby.Enums.Operator.Equals,
              },
            ] as Bixby.Models.UserActivityFilter[];
          }
        } else {
          return {
            field,
            value: filter,
            operator: Bixby.Enums.Operator.Equals,
          } as Bixby.Models.UserActivityFilter;
        }
      })
      .filter(Boolean);
  };

  const onGridReady = params => {
    const dataSource = {
      rowCount: null,
      getRows: async params => {
        try {
          const { startRow, filterModel }: GetRowsParams = params.request;

          const filters = filterModelToFilters(filterModel);
          const response = await UserActivityService.getByClientForCompliance(startRow, pageSize, filters);
          if (response) {
            params.success({
              rowData: response.results,
              rowCount: response.totalResults,
            });
            setTotalResults(response.totalResults);
          } else {
            setTotalResults(0);
            params.fail();
          }
        } catch (error) {
          dispatchToastError('Error fetching user activity history');
          setTotalResults(0);
          params.fail();
        }
      },
    };

    params.api.setServerSideDatasource(dataSource);
    setGridApi(params.api);
  };

  const downloadGrid = async () => {
    if (!gridApi) return;

    const filters = filterModelToFilters(gridApi.getFilterModel());
    try {
      setIsDownloading(true);

      const { blob } = await UserActivityService.downloadGrid(filters);

      if (profile) {
        AnalyticsService.event(Bixby.Constants.APPINSIGHTS_USERACTIVITY_DOWNLOAD, {
          ...profile,
          filters,
        });
      }
      dispatchDownloadData(blob, 'user-activity.csv');
    } catch (err) {
      dispatchToastError('Failed to download grid');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFilter = (id: string, futureState: boolean) => {
    const newFilters = declarationFilters.map(_ => ({ ..._, selected: _.id === id ? futureState : false }));
    setDeclarationFilters(newFilters);
    if (gridApi) {
      if (id == PUBLIC_DECLARATION_LABEL) {
        const currentFilterModel = gridApi.getFilterModel();
        currentFilterModel.company_DeclarationType = {
          filterType: 'set',
          values: ['Public'],
        };
        gridApi.setFilterModel(currentFilterModel);
      } else if (id == PRIVATE_DECLARATION_LABEL) {
        const currentFilterModel = gridApi.getFilterModel();
        currentFilterModel.company_DeclarationType = {
          filterType: 'set',
          values: ['Private'],
        };
        gridApi.setFilterModel(currentFilterModel);
      } else {
        const currentFilterModel = gridApi.getFilterModel();
        currentFilterModel.company_DeclarationType = '';
        gridApi.setFilterModel(currentFilterModel);
      }
    }
  };

  return (
    <section className="compliance">
      <div className="download_button_contain">
        <div className="declaration_filter_buttons">
          <FilterButtons filters={declarationFilters} onFilter={handleFilter} />
        </div>
        {totalResults > 0 && (
          <BixButton
            className="download_button"
            type="button"
            onClick={downloadGrid}
            useLoading
            isLoading={isDownloading}
            disabled={isDownloading}>
            <DownloadIcon /> <span>Download</span>
          </BixButton>
        )}
      </div>
      <div className="user_activity_grid">
        <div className="grid_container">
          <div className="grid ag-theme-alpine">
            <AgGridReact
              enableRangeSelection={true}
              defaultColDef={{
                flex: 1,
                minWidth: 100,
                sortable: false,
                resizable: true,
                lockPinned: true,
                cellClass: 'align-left',
                filter: SimpleTextFilter,
                filterParams: {
                  debounceMs: 1000,
                },
              }}
              reactiveCustomComponents
              suppressDragLeaveHidesColumns={true}
              suppressMovableColumns={true}
              animateRows={true}
              rowHeight={32}
              headerHeight={32}
              gridOptions={{
                getRowClass: params => (params.data?.['isActive'] ? '' : 'inactive'),
              }}
              onGridReady={onGridReady}
              columnDefs={[
                {
                  headerName: 'User',
                  cellClass: 'align-left',
                  field: 'user_Email',
                  filter: SimpleTextFilter,
                  filterParams: {
                    debounceMs: 1000,
                  },
                },
                {
                  headerName: 'Company',
                  cellClass: 'align-left',
                  field: 'company_Name',
                  filter: SimpleTextFilter,
                  filterParams: {
                    debounceMs: 1000,
                  },
                },
                {
                  headerName: 'Company Declaration Type',
                  cellClass: 'align-left',
                  field: 'company_DeclarationType',
                  filter: 'agSetColumnFilter',
                  filterParams: {
                    suppressMultiSelect: true,
                    values: ['Private', 'Public'],
                  },
                },
                {
                  headerName: 'Event Type',
                  cellClass: 'align-left',
                  field: 'eventType',
                  filter: 'agSetColumnFilter',
                  filterParams: {
                    suppressMultiSelect: true,
                    values: [
                      'Download Company Document',
                      'Download Tearsheet/Primer',
                      'View Transaction Comps',
                      'View News Entry',
                      'User Logged In',
                      'View Company',
                      'View News',
                      'View Company Financial Sheet',
                      'View Company Transcripts',
                      'View Company transcript Entry',
                      'View Company Transcript Entry',
                      'Download Company Transcript Text',
                      'Download Company Transcript Audio',
                      'View Company Documents',
                      'View Company Comps',
                      'View Company PAI',
                      'Download News',
                      'Download Financial Model',
                      'View Analytics',
                      'View CLOs',
                      'View Historical Documents',
                      'User Logged In Excel',
                      'View Analystics Excel',
                    ],
                  },
                  cellRenderer: params => {
                    switch (params.data?.['eventType'] as Bixby.Enums.UserActivityEvent) {
                      case Bixby.Enums.UserActivityEvent.DownloadCompanyDocument:
                        return 'Download Company Document';
                      case Bixby.Enums.UserActivityEvent.DownloadFinancialTearsheetPrimerDocument:
                        return 'Download Tearsheet/Primer';
                      case Bixby.Enums.UserActivityEvent.ViewTransactionComps:
                        return 'View Transaction Comps';
                      case Bixby.Enums.UserActivityEvent.ViewNewsEntry:
                        return 'View News Entry';
                      case Bixby.Enums.UserActivityEvent.UserLoggedIn:
                        return 'User Logged In';
                      case Bixby.Enums.UserActivityEvent.ViewCompany:
                        return 'View Company';
                      case Bixby.Enums.UserActivityEvent.ViewNews:
                        return 'View News';
                      case Bixby.Enums.UserActivityEvent.ViewCompanyFinancialSheet:
                        return 'View Company Financial Sheet';
                      case Bixby.Enums.UserActivityEvent.ViewCompanyTranscripts:
                        return 'View Company Transcripts';
                      case Bixby.Enums.UserActivityEvent.ViewCompanyTranscriptEntry:
                        return 'View Company Transcript Entry';
                      case Bixby.Enums.UserActivityEvent.DownloadCompanyTranscriptText:
                        return 'Download Company Transcript Text';
                      case Bixby.Enums.UserActivityEvent.DownloadCompanyTranscriptAudio:
                        return 'Download Company Transcript Audio';
                      case Bixby.Enums.UserActivityEvent.ViewCompanyDocuments:
                        return 'View Company Documents';
                      case Bixby.Enums.UserActivityEvent.ViewCompanyComps:
                        return 'View Company Comps';
                      case Bixby.Enums.UserActivityEvent.ViewCompanyPai:
                        return 'View Company PAI';
                      case Bixby.Enums.UserActivityEvent.DownloadNews:
                        return 'Download News';
                      case Bixby.Enums.UserActivityEvent.DownloadFinancialModel:
                        return 'Download Financial Model';
                      case Bixby.Enums.UserActivityEvent.ViewAnalytics:
                        return 'View Analytics';
                      case Bixby.Enums.UserActivityEvent.ViewClos:
                        return 'View CLOs';
                      case Bixby.Enums.UserActivityEvent.ViewHistoricalDocuments:
                        return 'View Historical Documents';
                      case Bixby.Enums.UserActivityEvent.UserLoggedInExcel:
                        return 'User Logged In Excel';
                      case Bixby.Enums.UserActivityEvent.ViewAnalysticsExcel:
                        return 'View Analystics Excel';
                      default:
                        return '';
                    }
                  },
                },
                {
                  headerName: 'Document Name',
                  cellClass: 'align-left',
                  field: 'document_Name',
                  filter: SimpleTextFilter,
                  filterParams: {
                    debounceMs: 1000,
                  },
                },
                {
                  headerName: 'Time of Access',
                  cellClass: 'align-left',
                  field: 'timeOfAccess',
                  filter: 'agDateColumnFilter',
                  filterParams: {
                    filterOptions: ['equals', 'inRange'],
                    defaultOption: 'equals',
                    suppressAndOrCondition: true,
                    comparator: (filterLocalDateAtMidnight, cellValue) => {
                      const cellDate = new Date(cellValue);
                      if (cellDate < filterLocalDateAtMidnight) return -1;
                      if (cellDate > filterLocalDateAtMidnight) return 1;
                      return 0;
                    },
                  },
                  cellRenderer: params => dayjs(params.data?.['timeOfAccess']).format('MM/DD/YYYY hh:mm A'),
                },
                {
                  headerName: 'Document Declaration',
                  cellClass: 'align-left',
                  field: 'document_DeclarationType',
                  filter: 'agSetColumnFilter',
                  filterParams: {
                    suppressMultiSelect: true,
                    values: ['Public', 'Private'],
                  },
                },
                {
                  headerName: 'Original File Path',
                  cellClass: 'align-left',
                  field: 'document_OriginalFilePath',
                  filter: SimpleTextFilter,
                  filterParams: {
                    debounceMs: 1000,
                  },
                },
                {
                  headerName: 'Date Posted',
                  cellClass: 'align-left',
                  field: 'document_DatePosted',
                  filter: 'agDateColumnFilter',
                  filterParams: {
                    filterOptions: ['equals', 'inRange'],
                    defaultOption: 'equals',
                    suppressAndOrCondition: true,
                    comparator: (filterLocalDateAtMidnight, cellValue) => {
                      const cellDate = new Date(cellValue);
                      if (cellDate < filterLocalDateAtMidnight) return -1;
                      if (cellDate > filterLocalDateAtMidnight) return 1;
                      return 0;
                    },
                  },
                  cellRenderer: params => params.value && dayjs(params.value).format('MM/DD/YYYY hh:mm A'),
                },
              ]}
              pagination={true}
              paginationPageSize={pageSize}
              rowModelType="serverSide"
              cacheBlockSize={pageSize}></AgGridReact>
          </div>
        </div>
      </div>
    </section>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    profile: state.profileState.profile,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchDownloadData: (blob: Blob, fileName: string) => dispatch(downloadFile(blob, fileName)),
  dispatchToastError: (message: string) => dispatch(toastError(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserActivity);
